import type { SimplifiedSchemaField } from '@thecompaniesapi/shared/client'

export const useActionsStore = defineStore('actions', () => {
  const api = useApi()
  const { createToast } = useToasts()

  const actions = ref<Action[]>([])
  const selectedActionId = useRouteQuery<number | undefined>('action', undefined)
  const selectedAction = ref<Action | undefined>(undefined)

  const fetchActions = async (params?: {
    ids?: number[]
    listId?: number
    page?: number
    size?: number
    status?: ActionStatus
    type?: ActionType
  }) => {
    try {
      const { data } = await api.actions.fetch({
        ids: params?.ids,
        listId: params?.listId,
        page: params?.page,
        size: params?.size,
        status: params?.status,
        type: params?.type,
      })

      if (!params?.ids?.length && !params?.listId) {
        actions.value = data.actions
      }

      return data
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const requestAction = async (params: {
    companyId?: number
    domains?: string[]
    estimate?: boolean
    fields?: SimplifiedSchemaField[]
    job?: ActionJobType
    listId?: number
    names?: string[]
    promptId?: number
    query?: SegmentationCondition[]
    question?: string
    type?: ActionType
  }) => {
    try {
      const { data } = await api.actions.request({
        domains: params.domains,
        estimate: params.estimate,
        fields: params.fields,
        job: params.job,
        listId: params.listId,
        names: params.names,
        promptId: params.promptId,
        query: params.query,
        question: params.question,
        type: params.type || 'jobs:request',
      })

      // Do not store estimate actions
      if (params.estimate) {
        return data.actions as Action[]
      }

      addOrReplaceActions({ actions: data.actions })

      return data.actions as Action[]
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const retryAction = async (params: {
    actionId: number
  }) => {
    try {
      const { data } = await api.actions.retry(params.actionId)

      return data.action as Action
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  // Setters
  const addOrReplaceActions = (params: {
    actions: Action[]
  }) => {
    for (const action of params.actions) {
      const index = actions.value.findIndex(item => item.id === action.id)

      index === -1 ? actions.value.push(action) : actions.value.splice(index, 1, action)

      // Update selected action if it is the same as the action being added
      if (action.id === Number(selectedActionId.value)) {
        selectedAction.value = action
      }
    }
  }

  const resetStore = () => {
    actions.value = []
    selectedAction.value = undefined
    selectedActionId.value = undefined
  }

  watch(
    () => selectedActionId.value,
    async (newId) => {
      if (newId) {
        try {
          const { actions } = await fetchActions({ ids: [newId] })

          if (actions?.[0] && actions[0]?.type === 'jobs:request') {
            if (actions?.[0]?.data?.job === 'cleanup-list') {
              createToast({
                message: 'actionCannotDisplayResults',
                status: 'warning',
              })

              return
            }

            // We never set selected action if it is not a jobs:request.
            // Other types of actions cannot be displayed for now.
            selectedAction.value = actions[0]
          }
        }
        catch {
          selectedAction.value = undefined
          selectedActionId.value = undefined
        }

        return
      }

      selectedAction.value = undefined
      selectedActionId.value = undefined
    },
    { immediate: true },
  )

  return {
    actions,
    addOrReplaceActions,
    fetchActions,
    requestAction,
    resetStore,
    retryAction,
    selectedAction,
    selectedActionId,
  }
})
